import React, { useId, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import ScrollRow from './ScrollRow';
import './ui.css';

/**
 * The open tab, kept in the address (?tab=entries) so reloading, sharing the
 * link or coming back to the page opens the same tab. The first tab leaves
 * the address clean.
 *
 *   const [tab, setTab] = useUrlTab(['details', 'invites', 'entries']);
 */
export function useUrlTab(ids, param = 'tab') {
  const [params, setParams] = useSearchParams();
  const asked = params.get(param);
  const tab = ids.includes(asked) ? asked : ids[0];
  const setTab = (id) => {
    const next = new URLSearchParams(params);
    if (id === ids[0]) next.delete(param);
    else next.set(param, id);
    setParams(next, { replace: true });
  };
  return [tab, setTab];
}

/**
 * One row of tabs, underlined. tabs: [{ id, label }]. Arrow keys move between
 * them. Give the panel id={`${idPrefix}-panel`} and aria-labelledby from
 * tabId(id) if you want the full tab pattern; the row works without.
 */
export default function Tabs({ tabs, value, onChange, label }) {
  const prefix = useId();
  const refs = useRef({});
  const onKeyDown = (e) => {
    const i = tabs.findIndex(t => t.id === value);
    let next = null;
    if (e.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
    if (e.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
    if (e.key === 'Home') next = tabs[0];
    if (e.key === 'End') next = tabs[tabs.length - 1];
    if (!next) return;
    e.preventDefault();
    onChange(next.id);
    refs.current[next.id]?.focus();
  };
  return (
    <ScrollRow className="ui-tabs" role="tablist" aria-label={label} onKeyDown={onKeyDown} selected={value}>
      {tabs.map(t => (
        <button
          key={t.id}
          ref={el => { refs.current[t.id] = el; }}
          type="button"
          role="tab"
          id={`${prefix}-${t.id}`}
          className="ui-tab"
          aria-selected={t.id === value}
          tabIndex={t.id === value ? 0 : -1}
          onClick={() => onChange(t.id)}
        >
          {t.label}
        </button>
      ))}
    </ScrollRow>
  );
}

/**
 * Filter chips: pills that narrow a list, purple when on. chips: [{ id,
 * label }]. `value` is the id that's on (single) or a list of ids (multiple).
 */
export function FilterChips({ chips, value, onChange, multiple = false, label }) {
  const on = (id) => (multiple ? value.includes(id) : value === id);
  const toggle = (id) => {
    if (!multiple) onChange(id);
    else onChange(on(id) ? value.filter(v => v !== id) : [...value, id]);
  };
  return (
    <ScrollRow className="ui-chips" role="group" aria-label={label} selected={multiple ? undefined : value}>
      {chips.map(c => (
        <button key={c.id} type="button" className="ui-chip" aria-pressed={on(c.id)} onClick={() => toggle(c.id)}>
          {c.label}
        </button>
      ))}
    </ScrollRow>
  );
}
